import type { AdminBusinessDetail } from "@teamna/shared";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Button, ConfirmDialog, Field, NoticeCoin, showToast } from "@teamna/ui/components/index.ts";
import { ApiError } from "@teamna/web-client/api/client.ts";
import { getActingAs, setActingAs } from "../lib/act-as.ts";
import { useDeleteBusiness } from "../lib/cc-api.ts";
import { countText } from "./format.ts";

/**
 * The danger zone at the end of one business: "Delete business".
 *
 * Built so staff can make a test academy on production and remove it again, but it deletes any
 * business. It is red, it is last on the page, and it asks for the business name before the
 * button works. The API checks the name again (`@teamna/core/db/delete-business.ts`).
 */
export function DeleteBusinessCard({ business }: { business: AdminBusinessDetail }) {
  const [open, setOpen] = useState(false);

  return (
    <section
      aria-labelledby="danger-heading"
      className="mt-10 rounded-card border-2 border-cancelled bg-paper p-5 shadow-card"
    >
      <div className="flex items-center gap-2.5">
        <NoticeCoin tone="danger" />
        <h2 id="danger-heading" className="text-[17px] text-cancelled">
          Danger zone
        </h2>
      </div>
      <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 text-[14px] text-ink-2">
          <p className="font-semibold text-ink">Delete this business</p>
          <p className="mt-1">
            This deletes the business and all its data, for good. You cannot undo this. Use it only for a test
            business or a business that asked to leave Teamna.
          </p>
        </div>
        <Button variant="danger" onClick={() => setOpen(true)} className="shrink-0">
          Delete business
        </Button>
      </div>
      {open ? <DeleteDialog business={business} onClose={() => setOpen(false)} /> : null}
    </section>
  );
}

function DeleteDialog({ business, onClose }: { business: AdminBusinessDetail; onClose: () => void }) {
  const [typed, setTyped] = useState("");
  const remove = useDeleteBusiness();
  const navigate = useNavigate();
  const matches = typed.trim() === business.name.trim();
  const billingLive = business.billing !== null && business.billing.state !== "ended" && !business.demoExpiresAt;

  const confirm = () => {
    if (!matches) return;
    remove.mutate(
      { businessId: business.id, confirmName: typed },
      {
        onSuccess: (result) => {
          if (getActingAs()?.businessId === business.id) setActingAs(null);
          showToast(
            result.subscriptionCancelled
              ? `${business.name} is deleted. Its subscription is cancelled.`
              : `${business.name} is deleted`,
          );
          void navigate({ to: "/businesses" });
        },
      },
    );
  };

  return (
    <ConfirmDialog
      open
      title={`Delete ${business.name}?`}
      confirmLabel="Delete for good"
      cancelLabel="Keep the business"
      tone="danger"
      loading={remove.isPending}
      confirmDisabled={!matches}
      onConfirm={confirm}
      onCancel={onClose}
    >
      <div className="notice notice-danger mb-4 rounded-card px-4 py-3.5 text-[13px]">
        <p className="text-[14px] font-semibold">This cannot be undone.</p>
        <ul className="mt-1.5 list-disc space-y-1 pl-4">
          <li>
            Teamna deletes the business and everything in it: {countText(business.membersActive, "member", "members")},{" "}
            {countText(business.participants, "participant", "participants")},{" "}
            {countText(business.groups.length, "group", "groups")}, the sessions, bookings, payments, messages and the
            audit log.
          </li>
          <li>People keep their Teamna accounts. They lose their place in this business.</li>
          {business.demoExpiresAt ? (
            <li>This is a demo club, so its demo logins are deleted too.</li>
          ) : (
            <li>
              {billingLive
                ? "Teamna cancels its Teamna subscription now, with no refund."
                : "If it has a Teamna subscription, Teamna cancels it now, with no refund."}
            </li>
          )}
        </ul>
      </div>
      <Field
        label="Type the business name to confirm"
        hint={`Type it exactly, with the same capital letters: ${business.name}`}
        value={typed}
        onChange={(event) => setTyped(event.target.value)}
        autoComplete="off"
        spellCheck={false}
        error={remove.isError ? errorText(remove.error) : undefined}
      />
    </ConfirmDialog>
  );
}

function errorText(error: unknown): string {
  if (error instanceof ApiError && error.detail) return error.detail;
  return "Could not delete the business. Try again.";
}
